import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FormState,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import slugify from "slugify";

import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import {
  SCHEMA_TYPE_OPTIONS,
  SchemaTypeSelector,
} from "src/base-components/SchemaTypeSelector";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { CaseTemplateFormValues } from "src/cases/templates/caseTemplateFormUtils";
import { getStepById } from "src/cases/templates/formSteps";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { additionalInputKeyValidators } from "src/cases/templates/validators";
import { CaseTemplateAdditionalInputTypeEnum } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";

export const ReviewAdditionalInputsStep: React.FC = () => {
  const step = getStepById("review-additional-inputs");
  const form = useFormContext<CaseTemplateFormValues>();
  const { fields } = useFieldArray<
    CaseTemplateFormValues,
    "attributes.additional_inputs",
    "id"
  >({
    name: "attributes.additional_inputs",
  });

  const hasPermission = useCheckFieldPermission();

  return (
    <FormStepWrapper description={step.description} title={step.title}>
      {fields.length > 0 ? (
        <>
          <div className="grid grid-cols-8 gap-x-2 gap-y-3">
            <AdditionalInputRow
              inputDescription={
                <div className="text-gray-500 font-inter-normal-12px">
                  Description
                </div>
              }
              inputName={
                <div className="text-gray-500 font-inter-normal-12px">
                  Additional input
                </div>
              }
              inputType={
                <div className="text-gray-500 font-inter-normal-12px">Type</div>
              }
            />
            {fields.map((field, index) => {
              return (
                <AdditionalInputRow
                  key={field.id}
                  inputDescription={
                    <div className="relative">
                      <Input
                        data-loc={`additional-input-${index}-description`}
                        placeholder="Description"
                        {...form.register(
                          `attributes.additional_inputs.${index}.description`,
                          {
                            maxLength: {
                              value: DEFAULT_STRING_LENGTH,
                              message: `Description must be less than ${DEFAULT_STRING_LENGTH} characters`,
                            },
                          },
                        )}
                        disabled={
                          !hasPermission(
                            "edit",
                            `attributes.additional_inputs.${index}.description`,
                          )
                        }
                        errored={
                          !!getInputError(form.formState, index)?.description
                        }
                        fullWidth
                      />
                      {getInputError(form.formState, index)?.description && (
                        <ErrorHint>
                          {
                            getInputError(form.formState, index)?.description
                              ?.message
                          }
                        </ErrorHint>
                      )}
                    </div>
                  }
                  inputName={
                    <div className="relative">
                      <Input
                        data-loc={`additional-input-${index}-name`}
                        placeholder="Name"
                        {...form.register(
                          `attributes.additional_inputs.${index}.display_name`,
                          {
                            required: "Display name is required",
                            maxLength: {
                              value: DEFAULT_STRING_LENGTH,
                              message: `Display name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                            },
                            onChange: (e) => {
                              const { isDirty } = form.getFieldState(
                                `attributes.additional_inputs.${index}.key`,
                              );

                              if (!isDirty) {
                                const slug = slugify(e.target.value, {
                                  lower: true,
                                  strict: true,
                                  replacement: "_",
                                });
                                form.setValue(
                                  `attributes.additional_inputs.${index}.key`,
                                  slug,
                                );
                              }
                            },
                            deps: `attributes.additional_inputs.${index}.key`,
                          },
                        )}
                        disabled={
                          !hasPermission(
                            "edit",
                            `attributes.additional_inputs.${index}.display_name`,
                          )
                        }
                        errored={
                          !!getInputError(form.formState, index)
                            ?.display_name ||
                          !!getInputError(form.formState, index)?.key
                        }
                        fullWidth
                      />
                      {/* Hidden field for key with validation */}
                      <input
                        type="hidden"
                        {...form.register(
                          `attributes.additional_inputs.${index}.key`,
                          {
                            required: "Key is required",
                            maxLength: {
                              value: DEFAULT_STRING_LENGTH,
                              message: `Key must be less than ${DEFAULT_STRING_LENGTH} characters`,
                            },
                            validate: additionalInputKeyValidators,
                          },
                        )}
                      />
                      {getInputError(form.formState, index)?.display_name && (
                        <ErrorHint>
                          {
                            getInputError(form.formState, index)?.display_name
                              ?.message
                          }
                        </ErrorHint>
                      )}
                      {getInputError(form.formState, index)?.key && (
                        <ErrorHint>
                          {getInputError(form.formState, index)?.key?.message}
                        </ErrorHint>
                      )}
                    </div>
                  }
                  inputType={
                    <div className="flex items-center gap-2">
                      <Controller
                        name={`attributes.additional_inputs.${index}.type`}
                        render={({ field }) => (
                          <SchemaTypeSelector
                            dataLoc={`additional-input-${index}-type-selector`}
                            disabled={
                              !hasPermission(
                                "edit",
                                `attributes.additional_inputs.${index}.type`,
                              )
                            }
                            options={SCHEMA_TYPE_OPTIONS.filter((option) =>
                              Object.values(
                                CaseTemplateAdditionalInputTypeEnum,
                              ).includes(
                                option.key as CaseTemplateAdditionalInputTypeEnum,
                              ),
                            )}
                            value={field.value}
                            fullWidth
                            onChange={(value) => field.onChange(value)}
                          />
                        )}
                      />
                    </div>
                  }
                />
              );
            })}
          </div>
        </>
      ) : (
        <div className="h-110">
          <EmptyState
            dataLoc="review-additional-inputs-empty-state"
            description="Additional inputs are not defined for this template"
            headline="No additional case inputs yet"
            icon={faSearch}
          />
        </div>
      )}
    </FormStepWrapper>
  );
};

const AdditionalInputRow: React.FC<{
  inputName: React.ReactNode;
  inputDescription: React.ReactNode;
  inputType: React.ReactNode;
}> = ({ inputName, inputDescription, inputType }) => {
  return (
    <>
      <div className="col-span-2">{inputName}</div>
      <div className="col-span-4">{inputDescription}</div>
      <div className="col-span-2">{inputType}</div>
    </>
  );
};

const getInputError = (
  formState: FormState<CaseTemplateFormValues>,
  index: number,
) => {
  return formState.errors.attributes?.additional_inputs?.[index];
};
